import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { CreditCard, Calendar, AlertCircle, MessageSquare, Bot, Zap, Send, ChevronDown, ChevronUp, Wallet, ArrowUpCircle, ArrowDownCircle, RefreshCw, Copy, Clock, CheckCircle, XCircle, Download, Plus, FileText, Receipt, AlertTriangle, Ban, Filter } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { planosAPI } from '../services/planosService';
import { faturamentoAPI, walletAPI, walletClienteAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import QRCode from 'react-qr-code';

const statusConfig = {
  PENDENTE: { label: 'Pendente', cls: 'bg-amber-500/10 text-amber-500', icon: Clock },
  PAGA: { label: 'Pago', cls: 'bg-emerald-500/10 text-emerald-500', icon: CheckCircle },
  EXPIRADA: { label: 'Expirada', cls: 'bg-red-500/10 text-red-400', icon: XCircle },
  CANCELADA: { label: 'Cancelada', cls: 'bg-zinc-500/10 text-zinc-400', icon: XCircle },
};
const quickValues = [10, 25, 50, 100, 250, 500];
const refTipoLabel = { recarga: 'Recarga', campanha: 'Campanha', ajuste: 'Ajuste', estorno: 'Estorno', pix: 'Pix' };

const Planos = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const clienteId = user?.cliente_id;

  const [planos, setPlanos] = useState([]);
  const [usoAtual, setUsoAtual] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanos, setShowPlanos] = useState(false);
  const [saldo, setSaldo] = useState(0);
  const [extrato, setExtrato] = useState([]);
  const [extratoPage, setExtratoPage] = useState(1);
  const [extratoTotalPages, setExtratoTotalPages] = useState(1);
  const [extratoFiltro, setExtratoFiltro] = useState('todos');
  const [tab, setTab] = useState('resumo');
  const [faturas, setFaturas] = useState([]);
  const [cobrancas, setCobrancas] = useState([]);
  const [ciclo, setCiclo] = useState(null);
  const [modal, setModal] = useState(false);
  const [valor, setValor] = useState('');
  const [gerando, setGerando] = useState(false);
  const [cobrancaGerada, setCobrancaGerada] = useState(null);
  const [pago, setPago] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const [upgradeLoading, setUpgradeLoading] = useState(null);

  useEffect(() => {
    if (!cobrancaGerada?.codigoSolicitacao || pago) return;
    const interval = setInterval(async () => {
      try {
        const res = await walletClienteAPI.consultarCobranca(cobrancaGerada.codigoSolicitacao);
        if (res.data?.status === 'PAGA') {
          setPago(true); clearInterval(interval);
          const sRes = await walletAPI.getSaldo(clienteId).catch(() => ({ data: { saldo: 0 } }));
          setSaldo(Number(sRes.data?.saldo || 0));
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [cobrancaGerada?.codigoSolicitacao, pago, clienteId]);

  // Depende de clienteId: no F5 o AuthContext popula o user num tick posterior
  // ao mount. Rodar com [] disparava as queries com clienteId undefined e a
  // pagina quebrava sem dados. Roda quando o cliente estiver disponivel.
  useEffect(() => {
    if (clienteId) carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [planosRes, usoRes, resumoRes, saldoRes, extratoRes, cobRes, fatRes, cicloRes] = await Promise.all([
        planosAPI.list().catch(() => ({ data: [] })),
        planosAPI.getUso(clienteId).catch(() => ({ data: null })),
        faturamentoAPI.getResumo(clienteId).catch(() => ({ data: null })),
        walletAPI.getSaldo(clienteId).catch(() => ({ data: { saldo: 0 } })),
        walletAPI.getExtrato(clienteId, 20, 1).catch(() => ({ data: { data: [], totalPages: 1 } })),
        walletClienteAPI.listarCobrancas(clienteId).catch(() => ({ data: [] })),
        faturamentoAPI.getFaturas(clienteId).catch(() => ({ data: [] })),
        faturamentoAPI.getCicloAtual(clienteId).catch(() => ({ data: null })),
      ]);
      setPlanos(planosRes.data); setUsoAtual(usoRes.data); setResumo(resumoRes.data);
      setSaldo(Number(saldoRes.data?.saldo || 0));
      setExtrato(extratoRes.data?.data || []); setExtratoTotalPages(extratoRes.data?.totalPages || 1); setExtratoPage(1);
      setCobrancas(cobRes.data || []); setFaturas(fatRes.data || []); setCiclo(cicloRes.data);
      // Abrir planos automaticamente se é trial
      const planoAtualData = usoRes.data?.plano_atual;
      if (planoAtualData && parseFloat(planoAtualData.preco) === 0) setShowPlanos(true);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  const mudarPaginaExtrato = async (page) => {
    try {
      const res = await walletAPI.getExtrato(clienteId, 20, page);
      setExtrato(res.data?.data || []); setExtratoPage(page); setExtratoTotalPages(res.data?.totalPages || 1);
    } catch {}
  };

  const gerarRecarga = async () => {
    const v = parseFloat(valor);
    if (!v || v < 2.5) return toast.error('Valor mínimo: R$ 2,50');
    setGerando(true);
    try {
      const res = await walletClienteAPI.criarRecarga({ clienteId, valor: v });
      let cobrData = res.data;
      if (!cobrData.pixCopiaECola && cobrData.codigoSolicitacao) {
        try { const det = await walletClienteAPI.consultarCobranca(cobrData.codigoSolicitacao); if (det.data) cobrData = { ...cobrData, pixCopiaECola: det.data.pix_copia_e_cola, linhaDigitavel: det.data.linha_digitavel }; } catch {}
      }
      setCobrancaGerada(cobrData); setModal(false); setValor(''); carregarDados();
    } catch (e) {
      const msg = e.response?.data?.message;
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join(', ') : 'Erro ao gerar cobrança');
    } finally { setGerando(false); }
  };

  const downloadExtrato = async () => {
    try {
      const res = await faturamentoAPI.downloadExtratoPdf(clienteId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'extrato-nomoz.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar extrato'); }
  };

  const abrirFaturaPendente = (f) => {
    setPago(false);
    setCobrancaGerada({ valor: f.valor, dataVencimento: f.created_at?.split('T')[0], pixCopiaECola: f.pix_copia_e_cola, linhaDigitavel: f.linha_digitavel, codigoSolicitacao: f.codigo_solicitacao });
  };

  const solicitarUpgrade = async (planoId) => {
    setUpgradeLoading(planoId);
    try {
      const res = await walletClienteAPI.upgrade(planoId);
      setPago(false);
      setCobrancaGerada(res.data);
      toast.success('Cobrança gerada! Pague via Pix para ativar o plano.');
    } catch (e) {
      const msg = e.response?.data?.message;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao gerar cobrança de upgrade');
    } finally { setUpgradeLoading(null); }
  };

  const copyText = (text, field) => { navigator.clipboard.writeText(text); setCopiedField(field); setTimeout(() => setCopiedField(null), 2000); };
  const pct = (usado, limite) => limite === -1 ? 0 : Math.min((usado / limite) * 100, 100);
  const corBarra = (p) => p >= 90 ? 'bg-red-500' : p >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d + (d.includes?.('T') ? '' : 'T12:00:00')); return dt.toLocaleDateString('pt-BR'); };
  const fmtDateTime = (d) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  const card = `rounded-2xl p-5 ${darkMode ? 'bg-zinc-800/80 border border-zinc-700/50' : 'bg-white border border-gray-100 shadow-sm'}`;
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-zinc-400' : 'text-gray-500';
  const textT = darkMode ? 'text-zinc-500' : 'text-gray-400';

  if (loading) return (<Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-2 border-nomoz-orange border-t-transparent" /></div></Layout>);

  const planoAtual = usoAtual?.plano_atual;
  const uso = usoAtual?.uso;
  const isTrial = planoAtual && parseFloat(planoAtual.preco) === 0;
  const extratoFiltrado = extratoFiltro === 'todos' ? extrato : extrato.filter(t => t.referencia_tipo === extratoFiltro);

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Financeiro" subtitle="Plano, faturas, saldo e extrato" />

        {/* ── BANNER INADIMPLÊNCIA ── */}
        {ciclo?.suspenso && (
          <div className="rounded-2xl p-5 bg-gradient-to-r from-red-600 to-red-700 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Ban className="h-6 w-6" />
                <div>
                  <p className="font-bold text-lg">Conta suspensa por inadimplência</p>
                  <p className="text-red-100 text-sm">Seus serviços estão temporariamente indisponíveis. Regularize para continuar.</p>
                </div>
              </div>
              {ciclo.fatura_pendente && (
                <button onClick={() => abrirFaturaPendente(ciclo.fatura_pendente)}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-red-600 hover:bg-red-50 transition-all shrink-0">
                  Pagar agora
                </button>
              )}
            </div>
          </div>
        )}
        {ciclo?.inadimplente && !ciclo?.suspenso && (
          <div className="rounded-2xl p-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6" />
                <div>
                  <p className="font-bold text-lg">Pagamento pendente</p>
                  <p className="text-amber-100 text-sm">Regularize para evitar a suspensão da sua conta.</p>
                </div>
              </div>
              {ciclo.fatura_pendente && (
                <button onClick={() => abrirFaturaPendente(ciclo.fatura_pendente)}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-white text-amber-600 hover:bg-amber-50 transition-all shrink-0">
                  Pagar agora
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── SALDO + RECARGA ── */}
        <div className={`rounded-2xl p-5 ${darkMode ? 'bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-800/50' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="h-4 w-4 text-emerald-500" />
                <p className={`text-xs ${textT}`}>Saldo disponível</p>
              </div>
              <p className={`text-3xl font-bold ${saldo > 0 ? 'text-emerald-500' : 'text-red-500'}`}>R$ {saldo.toFixed(2)}</p>
              {saldo > 0 && saldo < 10 && (
                <p className="text-xs text-amber-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Saldo baixo — considere adicionar créditos</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={carregarDados} className={`p-2 rounded-lg transition ${darkMode ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
                <RefreshCw className={`h-4 w-4 ${textT}`} />
              </button>
              <button onClick={() => setModal(true)}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-500/20 flex items-center gap-2">
                <Plus className="h-4 w-4" /> Adicionar créditos
              </button>
            </div>
          </div>
        </div>

        {/* ── PLANO + USO ── */}
        {usoAtual && (
          <div className={card}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500 text-white mb-2">{planoAtual?.nome || 'Sem Plano'}</span>
                <p className={`text-2xl font-bold ${textP}`}>
                  {parseFloat(planoAtual?.preco || 0) === 0 ? 'Grátis' : `R$ ${parseFloat(planoAtual?.preco || 0).toFixed(2)}`}
                  {parseFloat(planoAtual?.preco || 0) > 0 && <span className={`text-sm font-normal ${textT}`}>/mês</span>}
                </p>
              </div>
              {parseFloat(planoAtual?.preco || 0) === 0 && ciclo ? (
                <div className="text-right">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    ciclo.dias_restantes <= 2 ? 'bg-red-500/10 text-red-500' : ciclo.dias_restantes <= 4 ? 'bg-amber-500/10 text-amber-500' : darkMode ? 'bg-zinc-700' : 'bg-gray-100'
                  }`}>
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs font-medium">{ciclo.dias_restantes <= 0 ? 'Trial expirado' : `${ciclo.dias_restantes} dia${ciclo.dias_restantes !== 1 ? 's' : ''} de teste restante${ciclo.dias_restantes !== 1 ? 's' : ''}`}</span>
                  </div>
                  <p className={`text-xs mt-1 ${textT}`}>Escolha um plano para continuar após o trial</p>
                </div>
              ) : ciclo && parseFloat(planoAtual?.preco || 0) > 0 ? (
                <div className="text-right">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                    <Calendar className={`h-4 w-4 ${textT}`} />
                    <span className={`text-xs ${textS}`}>{ciclo.dias_restantes} dias restantes</span>
                  </div>
                  <p className={`text-xs mt-1 ${textT}`}>Próx. fatura: {fmtDate(ciclo.fim)} · R$ {parseFloat(ciclo.plano_preco || planoAtual?.preco || 0).toFixed(2)}</p>
                </div>
              ) : null}
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <span className={`text-sm font-medium ${textP}`}>Mensagens processadas (IA/analytics)</span>
                <span className={`text-sm font-semibold ${textP}`}>
                  {uso?.mensagens_recebidas_processadas || 0}<span className={`font-normal ${textT}`}> / {uso?.limite_mensagens_recebidas === -1 ? '∞' : uso?.limite_mensagens_recebidas || 0}</span>
                </span>
              </div>
              <div className={`w-full rounded-full h-3 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                <div className={`h-3 rounded-full transition-all ${corBarra(uso?.percentual_usado_recebidas || 0)}`}
                  style={{ width: `${pct(uso?.mensagens_recebidas_processadas || 0, uso?.limite_mensagens_recebidas || 0)}%` }} />
              </div>
              {(uso?.percentual_usado_recebidas || 0) >= 80 && (
                <div className={`flex items-center mt-2 text-xs ${uso.percentual_usado_recebidas >= 100 ? 'text-red-500' : 'text-amber-500'}`}>
                  <AlertCircle className="h-3.5 w-3.5 mr-1" />
                  {uso.percentual_usado_recebidas >= 100 ? 'Limite atingido — IA e automações pausadas.' : `${Math.round(uso.percentual_usado_recebidas)}% do limite utilizado`}
                </div>
              )}
            </div>
            {resumo?.detalhamento?.length > 0 && (
              <div className={`flex flex-wrap gap-4 mt-4 pt-4 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                {[
                  { tipo: 'manual', icon: MessageSquare, label: 'Manuais', color: 'text-emerald-500' },
                  { tipo: 'ia', icon: Bot, label: 'IA', color: 'text-purple-500' },
                  { tipo: 'campanha', icon: Send, label: 'Campanhas', color: 'text-orange-500' },
                  { tipo: 'fluxo', icon: Zap, label: 'Fluxos', color: 'text-blue-500' },
                ].map(t => {
                  const d = resumo.detalhamento.find(x => x.tipo === t.tipo);
                  if (!d) return null;
                  return (<div key={t.tipo} className="flex items-center gap-1.5"><t.icon className={`h-3.5 w-3.5 ${t.color}`} /><span className={`text-xs ${textS}`}>{t.label}:</span><span className={`text-xs font-semibold ${textP}`}>{d.total}</span></div>);
                })}
              </div>
            )}
          </div>
        )}

        {/* ── PLANOS (trial: aparece aqui em cima) ── */}
        {isTrial && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {planos.filter(p => parseFloat(p.preco) > 0).map((plano) => (
              <div key={plano.id} className={`rounded-2xl p-5 flex flex-col ${card}`}>
                <h3 className={`text-lg font-bold ${textP}`}>{plano.nome}</h3>
                <div className="mt-1 mb-4"><span className={`text-2xl font-bold ${textP}`}>R$ {parseFloat(plano.preco).toFixed(2)}</span><span className={`text-xs ${textT}`}>/mês</span></div>
                <div className="space-y-1.5 text-sm flex-1">
                  {[
                    { v: plano.limite_canais_whatsapp, l: 'canal WhatsApp', lp: 'canais WhatsApp' },
                    { v: plano.limite_mensagens_recebidas_mes, l: 'msg processada/mês', lp: 'msgs processadas/mês' },
                    { v: plano.limite_usuarios, l: 'usuário', lp: 'usuários' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span>{item.v === -1 ? `${item.lp.charAt(0).toUpperCase() + item.lp.slice(1)} ilimitados` : `${item.v?.toLocaleString()} ${item.v === 1 ? item.l : item.lp}`}</div>
                  ))}
                  {plano.permite_ia && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> IA GPT-4</div>}
                  {plano.permite_instagram && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> Instagram</div>}
                </div>
                <div className="mt-auto pt-4">
                  <button onClick={() => solicitarUpgrade(plano.id)} disabled={upgradeLoading === plano.id}
                    className="w-full py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50">
                    {upgradeLoading === plano.id ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto"></div> : `Assinar por R$ ${parseFloat(plano.preco).toFixed(2)}/mês`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── TABS ── */}
        <div className="flex gap-1">
          {[
            { key: 'resumo', label: 'Resumo', icon: FileText },
            { key: 'faturas', label: 'Faturas', icon: Receipt },
            { key: 'extrato', label: 'Extrato Wallet', icon: Wallet },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                tab === t.key ? 'bg-nomoz-orange text-white' : darkMode ? 'text-zinc-400 hover:bg-zinc-800' : 'text-gray-500 hover:bg-gray-100'
              }`}><t.icon className="h-3.5 w-3.5" />{t.label}</button>
          ))}
        </div>

        {/* ── TAB RESUMO ── */}
        {tab === 'resumo' && (
          <div className="space-y-4">
            {/* Fatura pendente em destaque */}
            {ciclo?.fatura_pendente && (
              <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-amber-900/20 border border-amber-700/50' : 'bg-amber-50 border border-amber-200'}`}>
                <div className={`px-5 py-3 flex items-center gap-2 ${darkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>Fatura do plano pendente</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className={`text-2xl font-bold ${textP}`}>R$ {parseFloat(ciclo.fatura_pendente.valor).toFixed(2)}</p>
                      <p className={`text-xs ${textT}`}>{fmtDateTime(ciclo.fatura_pendente.created_at)}</p>
                    </div>
                    <button onClick={() => abrirFaturaPendente(ciclo.fatura_pendente)}
                      className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-nomoz-orange hover:bg-orange-600 transition-all">
                      Pagar fatura
                    </button>
                  </div>
                  {ciclo.fatura_pendente.pix_copia_e_cola && (
                    <div className={`flex items-center gap-3 p-3 rounded-xl ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                      <div className="bg-white p-2 rounded-lg shrink-0">
                        <QRCode value={ciclo.fatura_pendente.pix_copia_e_cola} size={80} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-medium mb-1 ${textS}`}>Pix copia e cola</p>
                        <p className={`text-xs font-mono truncate ${textT}`}>{ciclo.fatura_pendente.pix_copia_e_cola.substring(0, 60)}...</p>
                        <button onClick={() => copyText(ciclo.fatura_pendente.pix_copia_e_cola, 'pix-resumo')}
                          className={`mt-2 px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition ${
                            copiedField === 'pix-resumo' ? 'bg-emerald-500 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          }`}>
                          {copiedField === 'pix-resumo' ? <><CheckCircle className="h-3 w-3" /> Copiado!</> : <><Copy className="h-3 w-3" /> Copiar Pix</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cobranças pendentes (recargas) */}
            {cobrancas.filter(c => c.status === 'PENDENTE').length > 0 && (
              <div className={card}>
                <h3 className={`text-sm font-semibold mb-3 ${textP}`}>Cobranças pendentes</h3>
                {cobrancas.filter(c => c.status === 'PENDENTE').map(c => (
                  <div key={c.id} className={`flex items-center justify-between py-3 border-b last:border-0 cursor-pointer ${darkMode ? 'border-zinc-700/50 hover:bg-zinc-700/30' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}
                    onClick={() => { setPago(false); setCobrancaGerada({ valor: c.valor, dataVencimento: c.created_at?.split('T')[0], pixCopiaECola: c.pix_copia_e_cola, linhaDigitavel: c.linha_digitavel, codigoSolicitacao: c.codigo_solicitacao }); }}>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-amber-500" />
                      <div><p className={`text-sm ${textP}`}>{c.descricao}</p><span className={`text-xs ${textT}`}>{fmtDateTime(c.created_at)}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-amber-500/10 text-amber-500">Pendente</span>
                      <span className={`text-sm font-semibold ${textP}`}>R$ {parseFloat(c.valor).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Últimas transações */}
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-3 ${textP}`}>Movimentações recentes</h3>
              {extrato.length === 0 ? (
                <p className={`text-sm text-center py-8 ${textT}`}>Nenhuma movimentação no período</p>
              ) : (
                <div className="space-y-0">
                  {extrato.slice(0, 10).map(t => (
                    <div key={t.id} className={`flex items-center justify-between py-3 border-b last:border-0 ${darkMode ? 'border-zinc-700/50' : 'border-gray-100'}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {t.tipo === 'credito' ? <ArrowUpCircle className="h-4 w-4 text-emerald-500 shrink-0" /> : <ArrowDownCircle className="h-4 w-4 text-red-500 shrink-0" />}
                        <div className="min-w-0">
                          <p className={`text-sm truncate ${textP}`}>{t.descricao}</p>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              t.referencia_tipo === 'recarga' || t.referencia_tipo === 'pix' ? 'bg-emerald-500/10 text-emerald-500' :
                              t.referencia_tipo === 'campanha' ? 'bg-orange-500/10 text-orange-500' : 'bg-zinc-500/10 text-zinc-400'
                            }`}>{refTipoLabel[t.referencia_tipo] || t.referencia_tipo}</span>
                            <span className={`text-xs ${textT}`}>{fmtDateTime(t.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <span className={`text-sm font-semibold shrink-0 ml-3 ${t.tipo === 'credito' ? 'text-emerald-500' : 'text-red-500'}`}>
                        {t.tipo === 'credito' ? '+' : '-'} R$ {parseFloat(t.valor).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {extrato.length > 10 && (
                <button onClick={() => setTab('extrato')} className={`w-full text-center text-xs mt-3 pt-3 border-t ${darkMode ? 'border-zinc-700/50 text-zinc-400 hover:text-white' : 'border-gray-100 text-gray-500 hover:text-gray-900'} transition-colors`}>
                  Ver extrato completo →
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── TAB FATURAS ── */}
        {tab === 'faturas' && (
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-3 ${textP}`}>Faturas do plano</h3>
            {faturas.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className={`h-10 w-10 mx-auto mb-3 ${textT}`} />
                <p className={`text-sm ${textT}`}>Nenhuma fatura gerada ainda</p>
                <p className={`text-xs mt-1 ${textT}`}>Sua primeira fatura será gerada ao final do ciclo atual</p>
              </div>
            ) : (
              <div className="space-y-0">
                {faturas.map(f => {
                  const cfg = statusConfig[f.status] || statusConfig.PENDENTE;
                  const Icon = cfg.icon;
                  return (
                    <div key={f.id} className={`flex items-center justify-between py-4 border-b last:border-0 ${f.status === 'PENDENTE' ? 'cursor-pointer' : ''} ${darkMode ? 'border-zinc-700/50 hover:bg-zinc-700/30' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}
                      onClick={() => { if (f.status === 'PENDENTE') abrirFaturaPendente(f); }}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          f.status === 'PAGA' ? 'bg-emerald-500/10' : f.status === 'PENDENTE' ? 'bg-amber-500/10' : 'bg-red-500/10'
                        }`}><Icon className={`h-5 w-5 ${cfg.cls.split(' ')[1]}`} /></div>
                        <div>
                          <p className={`text-sm font-medium ${textP}`}>{f.descricao}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-xs ${textT}`}>{fmtDateTime(f.created_at)}</span>
                            {f.pago_em && <span className="text-xs text-emerald-500">· Pago em {fmtDate(f.pago_em)}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                        <span className={`text-base font-bold ${textP}`}>R$ {parseFloat(f.valor).toFixed(2)}</span>
                        {f.status === 'PENDENTE' && f.codigo_solicitacao && (
                          <button onClick={(e) => { e.stopPropagation(); (async () => {
                            try { const res = await walletClienteAPI.downloadPdf(f.codigo_solicitacao); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = 'fatura-nomoz.pdf'; a.click(); window.URL.revokeObjectURL(url); } catch { toast.error('Erro ao baixar PDF'); }
                          })(); }} className={`p-2 rounded-lg transition ${darkMode ? 'hover:bg-zinc-600' : 'hover:bg-gray-200'}`}>
                            <Download className={`h-4 w-4 ${textT}`} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB EXTRATO WALLET ── */}
        {tab === 'extrato' && (
          <div className={card}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-semibold ${textP}`}>Extrato da wallet</h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Filter className={`h-3.5 w-3.5 ${textT}`} />
                  {['todos', 'recarga', 'campanha', 'pix'].map(f => (
                    <button key={f} onClick={() => setExtratoFiltro(f)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                        extratoFiltro === f ? 'bg-nomoz-orange text-white' : darkMode ? 'text-zinc-400 hover:bg-zinc-700' : 'text-gray-500 hover:bg-gray-100'
                      }`}>{f === 'todos' ? 'Todos' : refTipoLabel[f] || f}</button>
                  ))}
                </div>
                <button onClick={downloadExtrato} className={`p-2 rounded-lg transition flex items-center gap-1 text-xs ${darkMode ? 'hover:bg-zinc-700 text-zinc-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                  <Download className="h-3.5 w-3.5" /> PDF
                </button>
              </div>
            </div>
            {extratoFiltrado.length === 0 ? (
              <p className={`text-sm py-8 text-center ${textT}`}>Nenhuma movimentação{extratoFiltro !== 'todos' ? ` do tipo "${refTipoLabel[extratoFiltro]}"` : ''}</p>
            ) : (
              <div className="space-y-0">
                {extratoFiltrado.map(t => (
                  <div key={t.id} className={`flex items-center justify-between py-3 border-b last:border-0 ${darkMode ? 'border-zinc-700/50' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {t.tipo === 'credito' ? <ArrowUpCircle className="h-4 w-4 text-emerald-500 shrink-0" /> : <ArrowDownCircle className="h-4 w-4 text-red-500 shrink-0" />}
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${textP}`}>{t.descricao}</p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            t.referencia_tipo === 'recarga' || t.referencia_tipo === 'pix' ? 'bg-emerald-500/10 text-emerald-500' :
                            t.referencia_tipo === 'campanha' ? 'bg-orange-500/10 text-orange-500' : 'bg-zinc-500/10 text-zinc-400'
                          }`}>{refTipoLabel[t.referencia_tipo] || t.referencia_tipo}</span>
                          <span className={`text-xs ${textT}`}>{fmtDateTime(t.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <span className={`text-sm font-semibold shrink-0 ml-3 ${t.tipo === 'credito' ? 'text-emerald-500' : 'text-red-500'}`}>
                      {t.tipo === 'credito' ? '+' : '-'} R$ {parseFloat(t.valor).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {extratoTotalPages > 1 && (
              <div className={`flex items-center justify-between pt-3 mt-3 border-t ${darkMode ? 'border-zinc-700/50' : 'border-gray-100'}`}>
                <span className={`text-xs ${textT}`}>Página {extratoPage} de {extratoTotalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => mudarPaginaExtrato(extratoPage - 1)} disabled={extratoPage <= 1}
                    className={`px-3 py-1 text-xs rounded-lg transition ${darkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-30' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-30'}`}>Anterior</button>
                  <button onClick={() => mudarPaginaExtrato(extratoPage + 1)} disabled={extratoPage >= extratoTotalPages}
                    className={`px-3 py-1 text-xs rounded-lg transition ${darkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-30' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-30'}`}>Próxima</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── VER PLANOS (só aparece pra planos pagos) ── */}
        {!isTrial && (<>
        <button onClick={() => setShowPlanos(!showPlanos)}
          className={`w-full flex items-center justify-between py-3 px-5 rounded-xl text-sm font-medium transition-all ${
            darkMode ? 'bg-zinc-800 border border-zinc-700/50 text-zinc-300 hover:border-zinc-600' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300 shadow-sm'
          }`}>
          <div className="flex items-center gap-2"><CreditCard className="h-4 w-4" /> Ver planos disponíveis</div>
          {showPlanos ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showPlanos && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {planos.map((plano) => {
              const isAtual = planoAtual?.id === plano.id;
              return (
                <div key={plano.id} className={`rounded-2xl p-5 transition-all ${isAtual ? darkMode ? 'bg-emerald-900/20 border-2 border-nomoz-orange' : 'bg-emerald-50 border-2 border-nomoz-orange' : card}`}>
                  {isAtual && <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500 text-white mb-3">Plano Atual</span>}
                  <h3 className={`text-lg font-bold ${textP}`}>{plano.nome}</h3>
                  <div className="mt-1 mb-4"><span className={`text-2xl font-bold ${textP}`}>R$ {parseFloat(plano.preco).toFixed(2)}</span><span className={`text-xs ${textT}`}>/mês</span></div>
                  <div className="space-y-1.5 text-sm">
                    {[
                      { v: plano.limite_canais_whatsapp, l: 'canal WhatsApp', lp: 'canais WhatsApp' },
                      { v: plano.limite_mensagens_mes, l: 'mensagem/mês', lp: 'mensagens/mês' },
                      { v: plano.limite_usuarios, l: 'usuário', lp: 'usuários' },
                    ].map((item, i) => (
                      <div key={i} className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span>{item.v === -1 ? `${item.lp.charAt(0).toUpperCase() + item.lp.slice(1)} ilimitados` : `${item.v?.toLocaleString()} ${item.v === 1 ? item.l : item.lp}`}</div>
                    ))}
                    {parseFloat(plano.creditos_meta_inclusos || 0) > 0 && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> R$ {parseFloat(plano.creditos_meta_inclusos).toFixed(0)} em créditos Meta inclusos</div>}
                    {plano.permite_ia && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> IA GPT-4 ({plano.limite_conversas_ia_mes === -1 ? 'ilimitado' : `${plano.limite_conversas_ia_mes} conversas/mês`})</div>}
                    {plano.permite_fluxos && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> {plano.limite_fluxos === -1 ? 'Fluxos ilimitados' : `Até ${plano.limite_fluxos} fluxos de automação`}</div>}
                    {plano.permite_relatorios_avancados && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> Analytics completo</div>}
                    {plano.permite_instagram && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">✓</span> Instagram</div>}
                    {plano.permite_inteligencia_competitiva && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">★</span> Inteligência competitiva</div>}
                    {plano.permite_monitoramento_hashtags && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">★</span> Monitoramento de hashtags</div>}
                    {plano.gerente_dedicado && <div className={`flex items-center gap-2 ${textS}`}><span className="text-emerald-500 text-xs">★</span> Gerente de conta dedicado</div>}
                  </div>
                  {!isAtual && parseFloat(plano.preco) > 0 && (
                    <button onClick={() => solicitarUpgrade(plano.id)} disabled={upgradeLoading === plano.id}
                      className="w-full mt-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50">
                      {upgradeLoading === plano.id ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto"></div> : `Assinar por R$ ${parseFloat(plano.preco).toFixed(2)}/mês`}
                    </button>
                  )}
                  {!isAtual && parseFloat(plano.preco) === 0 && <p className={`text-xs text-center mt-4 ${textT}`}>Plano trial</p>}
                </div>
              );
            })}
          </div>
        )}
      </>)}
      </div>

      {/* ══ MODAL: PAGAMENTO ══ */}
      {cobrancaGerada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setCobrancaGerada(null); setPago(false); carregarDados(); }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className={`relative w-full max-w-md rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-zinc-900' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            {pago ? (
              <div className="p-8 text-center">
                <div className="relative mx-auto w-20 h-20 mb-5">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
                  <div className="relative w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center"><CheckCircle className="h-10 w-10 text-emerald-500" /></div>
                </div>
                <h3 className={`text-xl font-bold mb-1 ${textP}`}>Pagamento confirmado!</h3>
                <p className="text-3xl font-bold text-emerald-500 my-3">+ R$ {parseFloat(cobrancaGerada.valor).toFixed(2)}</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${darkMode ? 'bg-zinc-800' : 'bg-gray-50'}`}>
                  <Wallet className="h-4 w-4 text-emerald-500" /><span className={`text-sm ${textS}`}>Novo saldo:</span><span className="text-sm font-bold text-emerald-500">R$ {saldo.toFixed(2)}</span>
                </div>
                <button onClick={() => { setCobrancaGerada(null); setPago(false); carregarDados(); }}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all">Continuar</button>
              </div>
            ) : (
              <>
              <div className={`px-6 pt-6 pb-4 ${darkMode ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>Aguardando pagamento</span>
                  <span className={`text-xs ${textT}`}>Vence em {fmtDate(cobrancaGerada.dataVencimento)}</span>
                </div>
                <p className="text-3xl font-bold text-emerald-500">R$ {parseFloat(cobrancaGerada.valor).toFixed(2)}</p>
              </div>
              <div className="p-6 space-y-5">
                {cobrancaGerada.pixCopiaECola && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>1</div>
                      <span className={`text-sm font-semibold ${textP}`}>Pague via Pix</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-medium">Instantâneo</span>
                    </div>
                    <div className={`rounded-xl p-4 text-center ${darkMode ? 'bg-zinc-800' : 'bg-gray-50'}`}>
                      <div className="inline-block bg-white p-3 rounded-xl shadow-sm mb-3"><QRCode value={cobrancaGerada.pixCopiaECola} size={160} /></div>
                      <p className={`text-xs mb-3 ${textT}`}>Escaneie com o app do seu banco</p>
                      <button onClick={() => copyText(cobrancaGerada.pixCopiaECola, 'pix')}
                        className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${copiedField === 'pix' ? 'bg-emerald-500 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}>
                        {copiedField === 'pix' ? <><CheckCircle className="h-4 w-4" /> Copiado!</> : <><Copy className="h-4 w-4" /> Copiar código Pix</>}
                      </button>
                    </div>
                  </div>
                )}
                {cobrancaGerada.linhaDigitavel && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-600'}`}>2</div>
                      <span className={`text-sm font-semibold ${textP}`}>Ou pague via Boleto</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-gray-100 text-gray-500'} font-medium`}>1-3 dias úteis</span>
                    </div>
                    <div className={`rounded-xl p-4 ${darkMode ? 'bg-zinc-800' : 'bg-gray-50'}`}>
                      <p className={`text-xs font-mono break-all leading-relaxed mb-3 ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>{cobrancaGerada.linhaDigitavel}</p>
                      <div className="flex gap-2">
                        <button onClick={() => copyText(cobrancaGerada.linhaDigitavel, 'boleto')}
                          className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${copiedField === 'boleto' ? 'bg-emerald-500 text-white' : darkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                          {copiedField === 'boleto' ? <><CheckCircle className="h-3.5 w-3.5" /> Copiado!</> : <><Copy className="h-3.5 w-3.5" /> Copiar código</>}
                        </button>
                        {cobrancaGerada.codigoSolicitacao && (
                          <button onClick={async () => { try { const res = await walletClienteAPI.downloadPdf(cobrancaGerada.codigoSolicitacao); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = 'boleto-nomoz.pdf'; a.click(); window.URL.revokeObjectURL(url); } catch { toast.error('Erro ao baixar PDF'); } }}
                            className="flex-1 py-2 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 bg-nomoz-orange hover:bg-orange-600 text-white">
                            <Download className="h-3.5 w-3.5" /> Baixar PDF
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className={`flex items-center justify-center gap-2.5 py-3 rounded-xl ${darkMode ? 'bg-zinc-800/50' : 'bg-gray-50'}`}>
                  <div className="relative h-4 w-4"><div className="absolute inset-0 rounded-full border-2 border-emerald-500/30" /><div className="absolute inset-0 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>
                  <span className={`text-xs ${textS}`}>Aguardando confirmação do pagamento...</span>
                </div>
                <button onClick={() => { setCobrancaGerada(null); setPago(false); carregarDados(); }}
                  className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'text-zinc-400 hover:text-zinc-300' : 'text-gray-500 hover:text-gray-700'}`}>Fechar</button>
              </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══ MODAL: ADICIONAR CRÉDITOS ══ */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className={`relative w-full max-w-sm rounded-2xl overflow-hidden ${darkMode ? 'bg-zinc-900' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`px-6 pt-6 pb-4 text-center ${darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${darkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}><Wallet className="h-7 w-7 text-emerald-500" /></div>
              <h3 className={`text-lg font-bold ${textP}`}>Adicionar créditos</h3>
              <p className={`text-xs mt-1 ${textT}`}>Escolha ou digite o valor da recarga</p>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-3 gap-2">
                {quickValues.map(v => (
                  <button key={v} onClick={() => setValor(String(v))}
                    className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${valor === String(v) ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : darkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'}`}>R$ {v}</button>
                ))}
              </div>
              <div>
                <label className={`text-xs font-medium ${textS}`}>Ou digite um valor personalizado</label>
                <div className={`relative mt-1.5 rounded-xl overflow-hidden border-2 transition-colors ${darkMode ? 'border-zinc-700 focus-within:border-emerald-500' : 'border-gray-200 focus-within:border-emerald-500'}`}>
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-base font-semibold ${textS}`}>R$</span>
                  <input type="text" inputMode="decimal" value={valor}
                    onChange={e => setValor(e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'))}
                    placeholder="0,00"
                    className={`w-full pl-12 pr-4 py-3.5 text-xl font-bold bg-transparent outline-none ${darkMode ? 'text-white placeholder-zinc-600' : 'text-gray-900 placeholder-gray-300'}`}
                    autoFocus />
                </div>
                <p className={`text-xs mt-1.5 ${textT}`}>Mínimo R$ 2,50</p>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setModal(false)}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Cancelar</button>
                <button onClick={gerarRecarga} disabled={gerando || !valor}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-emerald-500/20">
                  {gerando ? <span className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />Gerando...</span> : 'Gerar cobrança'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Planos;
